import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { AdminAccountApi } from '../api/admin-account.api';
import { AuthService } from './auth.service';

/** The permission areas the admin controllers gate on (`host.<area>`, `.view`, `.manage`). */
export type PermissionArea = 'tenants' | 'plans' | 'promoCodes' | 'analytics' | 'operators' | 'leads';
export type PermissionLevel = 'view' | 'manage';

/** A hung `/me` must not block navigation; on timeout the error branch keeps the previous keys. */
const ME_TIMEOUT_MS = 5000;

/**
 * Mirrors the backend `PermissionExpansion.IsSatisfiedBy`: a held key satisfies `required` when it equals it or is
 * one of its dot-prefixes (case-insensitive). A held `host.tenants.view` never satisfies `host.tenants` or `.manage`.
 */
export function isPermissionSatisfied(held: readonly string[], required: string): boolean {
  const set = new Set(held.map((k) => k.trim().toLowerCase()).filter(Boolean));
  let current = required.trim().toLowerCase();
  while (current.length > 0) {
    if (set.has(current)) return true;
    const idx = current.lastIndexOf('.');
    if (idx <= 0) break;
    current = current.slice(0, idx);
  }
  return false;
}

/**
 * The signed-in operator's permission keys, from `GET admin/AdminAccount/me`, for hiding what the backend would refuse.
 * Until the keys are known (not loaded yet, or the load failed) everything is allowed: the backend stays the authority.
 */
@Injectable({ providedIn: 'root' })
export class OperatorPermissions {
  private readonly api = inject(AdminAccountApi);
  private readonly auth = inject(AuthService);
  private readonly injector = inject(Injector);

  private readonly keys = signal<string[] | null>(null);
  /** The access token the current keys were loaded with. */
  private loadedFor: string | null = null;
  private inflight: { token: string; promise: Promise<void> } | null = null;
  private watching = false;

  readonly loaded = computed(() => this.keys() !== null);

  can(required: string): boolean {
    const held = this.keys();
    return held === null || isPermissionSatisfied(held, required);
  }

  /** Class-level gate of the area's controller: `host.X`, `host.X.view` or `host.X.manage`. */
  canView(area: PermissionArea): boolean {
    return this.can(`host.${area}.view`) || this.can(`host.${area}.manage`);
  }

  /** Mutation gate of the area's controller: `host.X` or `host.X.manage`. */
  canManage(area: PermissionArea): boolean {
    return this.can(`host.${area}.manage`);
  }

  allows(area: PermissionArea, level: PermissionLevel): boolean {
    return level === 'manage' ? this.canManage(area) : this.canView(area);
  }

  setKeys(keys: string[] | null): void {
    this.keys.set(keys ? [...keys] : null);
  }

  clear(): void {
    this.keys.set(null);
    this.loadedFor = null;
    this.inflight = null;
  }

  /** Loads the keys for the current access token unless they are already loaded (or loading) for it. Never rejects. */
  ensureLoaded(): Promise<void> {
    const token = this.auth.getAccessToken();
    // No session: nothing to load (sign-out clears the keys through `watchSession`).
    if (!token) return Promise.resolve();
    if (this.loadedFor === token) return Promise.resolve();
    if (this.inflight?.token === token) return this.inflight.promise;

    const promise = firstValueFrom(this.api.me().pipe(timeout(ME_TIMEOUT_MS)))
      .then(
        (profile) => {
          // Signed out while the request was in flight: do not resurrect the keys.
          if (!this.auth.getAccessToken()) return;
          this.keys.set(profile.permissions ?? []);
          this.loadedFor = this.auth.getAccessToken();
        },
        () => {
          // Keep the previous keys; with none, everything stays allowed and the backend answers 403 where needed.
        },
      )
      .finally(() => {
        if (this.inflight?.promise === promise) this.inflight = null;
      });
    this.inflight = { token, promise };
    return promise;
  }

  /**
   * Keeps the keys in step with the session: loads them when a session appears or its access token changes (sign-in,
   * refresh, another tab) and forgets them on sign-out. Called once by the app initializer.
   */
  watchSession(): void {
    if (this.watching) return;
    this.watching = true;
    effect(
      () => {
        const token = this.auth.session()?.accessToken ?? null;
        untracked(() => (token ? void this.ensureLoaded() : this.clear()));
      },
      { injector: this.injector },
    );
  }
}
